import { useCallback } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import type { IMappedFilterValue, ISearchFilter } from '../../common-types';
import { FilterMenuItem } from './FilterMenuItem';
import { getFilterName, normalizeFilterName } from '../__feature__';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 400,
    },
  },
};

interface ArrayFilterProps {
  filter: ISearchFilter;
  selectedOptions: string[];
  handleSelectedChange: (selectedOptions: string[]) => void;
  testId?: string;
}

export const ArrayFilter = ({
  filter,
  selectedOptions,
  handleSelectedChange,
  testId,
}: ArrayFilterProps) => {
  const isStringArrayFilter = filter.dataType === 'StringArray';

  const handleChange = useCallback(
    (event: SelectChangeEvent<typeof selectedOptions>) => {
      const {
        target: { value },
      } = event;

      handleSelectedChange(
        typeof value === 'string' ? value.split(',') : value
      );
    },
    [handleSelectedChange]
  );

  const getRenderValue = useCallback(
    (selected: string[]) => {
      if (isStringArrayFilter) {
        if (filter.isValueTranslationRequired && filter.filterValues?.length) {
          const valueToTranslationMap = new Map(
            filter.filterValues.map(({ value, translatedValue }) => [
              value,
              translatedValue || value,
            ])
          );
          return selected
            .map((val) => valueToTranslationMap.get(val) || val)
            .join(', ');
        }
      }
      return selected.join(', ');
    },
    [filter?.filterName]
  );

  const renderFilterValues = () => {
    if (isStringArrayFilter && Array.isArray(filter?.filterValues)) {
      const sortedFilterValues: IMappedFilterValue[] = [
        ...filter.filterValues,
      ].sort((a, b) => a.value.localeCompare(b.value));

      return sortedFilterValues.map((option, index) => {
        const displayValue =
          filter.isValueTranslationRequired && option.translatedValue?.trim()
            ? option.translatedValue
            : option.value;

        return (
          <MenuItem
            key={option.value}
            value={option.value}
            data-testid={`filter-menu-item-${filter?.filterName}-${index}`}
          >
            <FilterMenuItem
              option={displayValue}
              isChecked={selectedOptions.includes(option.value)}
            />
          </MenuItem>
        );
      });
    }
    return null;
  };

  return (
    <>
      {isStringArrayFilter && (
        <StyledFormControl data-testid={testId} fullWidth size="small">
          <InputLabel
            id={`filter-label-${normalizeFilterName(filter?.filterName)}`}
          >
            {getFilterName(filter?.filterName)}
          </InputLabel>
          <Select
            labelId={`filter-label-${normalizeFilterName(filter?.filterName)}`}
            multiple
            value={selectedOptions}
            onChange={handleChange}
            label={getFilterName(filter?.filterName)}
            renderValue={getRenderValue}
            variant="outlined"
            MenuProps={MenuProps}
            data-testid={`string-array-filter-${normalizeFilterName(
              filter?.filterName
            )}`}
          >
            {renderFilterValues()}
          </Select>
        </StyledFormControl>
      )}
    </>
  );
};
